import React, { useState, useRef } from 'react';
import './AccessibilityTools.css';

const AccessibilityTools = () => {
  const [menuVisible, setMenuVisible] = useState(false);
  const overlayRef = useRef(null);
  const maskRef = useRef(null);
  const currentOpacity = useRef('');
  const currentTop = useRef('25vh');
  const currentBottom = useRef('32vh');
  
  // reset toggles
  const [activeFontIndex, setActiveFontIndex] = useState(null);
  const [activeTextSizesIndex, setActiveTextSizesIndex] = useState(null);
  const [activeLineHeightsIndex, setActiveLineHeightsIndex] = useState(null);
  const [activeColorOverlaysIndex, setActiveColorOverlaysIndex] = useState(null);
  const [activeReadingMaskIndex, setActiveReadingMaskIndex] = useState(null);
  const [activeReadingMaskSizeIndex, setActiveReadingMaskSizeIndex] = useState(null);

  
  
  const toggleMenu = () => setMenuVisible(prev => !prev);

  const fontList = [
    { name: 'Lexend', style: '"Lexend", sans-serif' },
    { name: 'Roboto', style: '"Roboto", sans-serif' },
    { name: 'Montserrat', style: 'Montserrat, serif' }
  ];
  
  const textSizes = [
    { label: 'S', size: '1.3rem' },
    { label: 'M', size: '1.5rem' },
    { label: 'L', size: '1.7rem' }
  ];
  
  const lineHeights = [
    { label: '2x', size: '2.5rem' },
    { label: '3x', size: '4rem' },
    { label: '4x', size: '4.5rem' }
  ];
  
  const colorOverlays = [
    { label: 'Red', color: 'rgba(255,0,0,0.4)' },
    { label: 'Green', color: 'rgba(15,187,20,0.4)' },
    { label: 'Blue', color: 'rgba(0,0,255,0.4)' }
  ];
  
  const readingMaskOpacities = [
    { label: 'Light', opacity: 'rgba(0, 0, 0, 0.4)' },
    { label: 'Medium', opacity: 'rgba(0, 0, 0, 0.7)' },
    { label: 'Dark', opacity: 'rgba(0, 0, 0, 0.9)' }
  ];
  
  const readingMaskSizes = [
    { label: 'S', top: '250px', bottom: '250px' },
    { label: 'M', top: '150px', bottom: '150px' },
    { label: 'L', top: '50px', bottom: '50px' }
  ];

  const showOverlay = (color) => {
    if (!overlayRef.current) {
      const div = document.createElement('div');
      div.style.position = 'fixed';
      div.style.top = 0;
      div.style.left = 0;
      div.style.width = '100vw';
      div.style.height = '100vh';
      div.style.zIndex = 2;
      div.style.pointerEvents = 'none';
      document.body.appendChild(div);
      overlayRef.current = div;
    }
    overlayRef.current.style.backgroundColor = color;
  };

  const showReadingMask = (opacity, top = currentTop.current, bottom = currentBottom.current) => {
    if (!maskRef.current) {
      const div = document.createElement('div');
      div.className = 'reading-mask';
      document.body.appendChild(div);
      maskRef.current = div;
    }
  
    // Animate opacity and border width
    maskRef.current.style.borderTop = `${top} solid ${opacity}`;
    maskRef.current.style.borderBottom = `${bottom} solid ${opacity}`;
    maskRef.current.classList.add('visible');
  
    currentOpacity.current = opacity;
  };

  const updateReadingMaskSize = (top, bottom) => {
    currentTop.current = top;
    currentBottom.current = bottom;
    if (maskRef.current && currentOpacity.current) {
      maskRef.current.style.borderTop = `${top} solid ${currentOpacity.current}`;
      maskRef.current.style.borderBottom = `${bottom} solid ${currentOpacity.current}`;
    }
  };

  return (
    <div className='accMenuh3'>
      <button id="accessibilityButton" onClick={toggleMenu}>
        <span className="material-symbols-outlined" style={{ marginRight: '0.5rem' }}>
          settings_accessibility
        </span>
        Accessibility Tools
      </button>

      <div
        className="AccessibilityMenuCSS"
        style={{ right: menuVisible ? '0px' : '-350px' }}
      >
        <div style={{ backgroundColor: '#004c8c', padding: '10px' }}>
          <button
            onClick={() => {
              // Reset font
              document.body.style.fontFamily = '';
              document.body.style.fontSize = '';
              document.querySelectorAll('p').forEach(p => p.style.lineHeight = '');

              // Reset overlay
              if (overlayRef.current) {
                overlayRef.current.remove();
                overlayRef.current = null;
              }

              // Reset reading mask
              if (maskRef.current) {
                maskRef.current.remove();
                maskRef.current = null;
              }
              currentOpacity.current = '';
              currentTop.current = '25vh';
              currentBottom.current = '32vh';

              // Reset active states
              setActiveFontIndex(null);
              setActiveTextSizesIndex(null);
              setActiveLineHeightsIndex(null);
              setActiveColorOverlaysIndex(null);
              setActiveReadingMaskIndex(null);
              setActiveReadingMaskSizeIndex(null);
            }}  
            style={{
              backgroundColor: '#FFC72C',
              border: 'none',
              padding: '10px',
              fontWeight: 'bold',
              cursor: 'pointer',
              borderRadius: '4px',
              fontSize: '16px'
            }}
          >
            Reset All
          </button>
        </div>
        <div className='accMenuItems'>
          
        <div className='toolBox'>
          <h3>Reading Mask</h3>
          {/* Opacity Options */}
          <ul className="accMenuItemsTop">
            {readingMaskOpacities.map((item, index) => (
              <li
              key={index}
              className={activeReadingMaskIndex === index ? 'active' : ''}
              onClick={() => {
                if (!maskRef.current) {
                  const div = document.createElement('div');
                  div.className = 'reading-mask';
                  document.body.appendChild(div);
                  maskRef.current = div;
                }
                
                if (activeReadingMaskIndex === index) {
                  // Reset
                  maskRef.current.remove();
                  maskRef.current = null;
                  setActiveReadingMaskIndex(null);
                } else {
                  showReadingMask(item.opacity);
                  setActiveReadingMaskIndex(index);
                }
              }}
              >
                {item.label}
              </li>
            ))}
          </ul>
          {/* Size Options */}
          <ul className="accMenuItemsTop">
            {readingMaskSizes.map((item, index) => (
              <li
              key={index}
              className={activeReadingMaskSizeIndex === index ? 'active' : ''}
              onClick={() => {
                updateReadingMaskSize(item.top, item.bottom);
                setActiveReadingMaskSizeIndex(index);
              }}
              >
                {item.label}
              </li>
            ))}
          </ul>
        </div>

        <div className='toolBox'>
          <h3>Font</h3>
          <ul className="accMenuItemsTop">
            {fontList.map((item, index) => (
              <li
              key={index}
              className={activeFontIndex === index ? 'active' : ''}
              onClick={() => {
                if (activeFontIndex === index) {
                  document.body.style.fontFamily = '';
                  setActiveFontIndex(null); // Reset if clicked again
                } else {
                  document.body.style.fontFamily = item.style;
                  setActiveFontIndex(index); // Set new active
                }
              }}
              >
                {item.name}
              </li>
            ))}
          </ul>
        </div>

        <div className='toolBox'>
          <h3>Text Resize</h3>
          <ul className="accMenuItemsTop">
            {textSizes.map((item, index) => (
              <li
              key={index}
              className={activeTextSizesIndex === index ? 'active' : ''}
              onClick={() => {
                if (activeTextSizesIndex === index) {
                  document.body.style.fontSize = '';
                  setActiveTextSizesIndex(null); // Reset if clicked again
                } else {
                  document.body.style.fontSize = item.size;
                  setActiveTextSizesIndex(index); // Set new active
                }
              }}
              >
                {item.label}
              </li>
            ))}
          </ul>
        </div>

        <div className='toolBox'>
          <h3>Line Height</h3>
          <ul className="accMenuItemsTop">
            {lineHeights.map((item, index) => (
              <li
              key={index}
              className={activeLineHeightsIndex === index ? 'active' : ''}
              onClick={() => {
                if (activeLineHeightsIndex === index) {
                  document.querySelectorAll('p').forEach(p => {
                    p.style.lineHeight = '';
                  });
                  setActiveLineHeightsIndex(null); // Reset if clicked again
                } else {
                  document.querySelectorAll('p').forEach(p => {
                    p.style.lineHeight = item.size;
                  });
                  setActiveLineHeightsIndex(index); // Set new active
                }
              }}
              >
                {item.label}
              </li>
            ))}
          </ul>
        </div>

        <div className='toolBox'>
          <h3>Color Overlay</h3>
          <ul className="accMenuItemsTop">
            {colorOverlays.map((item, index) => (
              <li
              key={index}
              className={activeColorOverlaysIndex === index ? 'active' : ''}
              onClick={() => {
                if (!overlayRef.current) {
                  const overlay = document.createElement('div');
                  overlay.style.position = 'fixed';
                  overlay.style.top = 0;
                  overlay.style.left = 0;
                  overlay.style.width = '100vw';
                  overlay.style.height = '100vh';
                  overlay.style.pointerEvents = 'none';
                  overlay.style.zIndex = 2;
                  overlay.style.transition = 'opacity 0.3s ease';
                  document.body.appendChild(overlay);
                  overlayRef.current = overlay;
                }
                
                if (activeColorOverlaysIndex === index) {
                  overlayRef.current.remove();
                  overlayRef.current = null;
                  setActiveColorOverlaysIndex(null);
                } else {
                  overlayRef.current.style.backgroundColor = item.color;
                  overlayRef.current.style.display = 'block';
                  overlayRef.current.style.opacity = 1;
                  setActiveColorOverlaysIndex(index);
                }
              }}
              >
                {item.label}
              </li>
            ))}
          </ul>
            </div>
        </div>
      </div>
    </div>
  );
};

export default AccessibilityTools;


